/* eslint-disable consistent-return */
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
/* eslint-disable prefer-destructuring */
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import styled from 'styled-components';
import { AlertContent, ButtonIcon, Card, DurationInput, Icon, Input, ModalFactory, Text, Tooltip } from 'components';
import { Duration } from 'components/Duration';
import { IDurationResponse } from 'components/Input/DurationInput';
import { theme, toRem } from 'design';
import { EmployeeLaborJobErrorType, IEmployeeLaborJobError, IEmployeeLaborJobState } from 'screens/MakePayment/Calculator/types';
import { IconButton } from 'screens/MakePayment/components/PaymentsListItem/styles';
import { IPOSProvider } from 'types';
import { formatJobTitle } from 'utils/jobs';
import { TimecardModal } from '../../modals/TimecardModal/TimecardModal';
import UserInfo from '../../UserInfo/UserInfo';

export interface IEmployeeListItemProps {
  className?: string;
  isLoading?: boolean;
  laborData?: IEmployeeLaborJobState[];
  timecardData?: IPOSProvider;
  paymentDate?: string;
  timezone?: string;
  userId: string;
  username: string;
  removeDisabled?: boolean;
  isShareRemovable?: boolean;
  autoClockOut?: boolean;
  hasSplitChecks?: boolean;
  onRemoveEmployee: (userId?: string) => void;
  onJobChange: (jobData: IEmployeeLaborJobState[], deletedJob?: IEmployeeLaborJobState) => void;
  validate: (jobData: IEmployeeLaborJobState[]) => IEmployeeLaborJobState[];
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: auto;
`;

const TopRow = styled(Row)`
  padding: ${toRem(22)} ${toRem(22)} 0;
`;

const BottomRow = styled(Row)`
  padding: ${toRem(16)} ${toRem(22)} ${toRem(22)};
`;

const EmployeeListItemLayout = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  background: ${theme.color.neutral[50]};
  overflow: visible;

  :not(:last-child) {
    margin-bottom: ${toRem(20)};
  }
`;

const ErrorRow = styled.div`
  display: flex;
  padding: ${toRem(8)} ${toRem(20)};

  justify-content: center;
  text-align: center;

  &.error-0 {
    background-color: ${theme.color.neutral[200]};
  }

  &.error-1,
  &.error-2 {
    background-color: ${theme.color.warning.light};
  }

  :first-child {
    border-top-right-radius: ${toRem(11)};
    border-top-left-radius: ${toRem(11)};
  }
`;

const ErrorList = styled.ul`
  list-style: none;
`;

const ErrorListItem = styled.li`
  &:not(:first-child) {
    padding-top: ${toRem(8)};
  }
`;

const JobRow = styled(Row)`
  flex-direction: column;
  width: 100%;

  &.m-row {
    display: flex;
  }

  &.dt-row {
    display: none;
  }

  &:not(.header-row) {
    padding: ${toRem(12)};
    background-color: ${theme.color.neutral[100]};
    border: 1px solid ${theme.color.neutral[200]};
    border-radius: ${toRem(8)};
  }

  &:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    --job-row-size: 33%;

    &.removable {
      --job-row-size: calc(33% - ${toRem(12)});
    }

    & > *:not(.remove-job-button) {
      max-width: var(--job-row-size);
      min-width: var(--job-row-size);
    }

    &.m-row {
      display: none;
    }

    &.dt-row {
      display: flex;
    }

    &:not(.header-row) {
      padding: 0;
      background: none;
      border: none;
      border-radius: 0;
    }

    &:not(:last-child) {
      margin-bottom: ${toRem(8)};
    }
  }
`;

const JobsContainer = styled.div`
  flex-direction: column;
  width: 100%;
`;

const JobsRowHeader = styled.span`
  font-size: ${toRem(12)};
  font-weight: 500;
  color: ${theme.color.neutral[500]};
`;

const JobFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-bottom: ${toRem(12)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;

    &:not(:last-child) {
      margin-bottom: 0;
    }
  }
`;

const RemoveJobButton = styled.button`
  margin: 0 auto;
  font-size: ${toRem(11)};
  font-weight: 600;
  color: ${theme.color.danger.medium};
  background: none;
  border: none;
  outline: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: none;
    margin: 0;
  }
`;

const RemoveJobIconButton = styled(IconButton)`
  display: none;
  background: none;
  border: none;

  &:hover,
  &:focus-visible {
    background: none;

    svg {
      fill: ${theme.color.danger.medium};
    }
  }

  &:focus-visible {
    outline: 1px dashed ${theme.color.neutral[800]};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
    margin: 0;
  }
`;

const SecondaryControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: ${toRem(8)};
`;

const StyledDurationInput = styled(DurationInput)`
  &.error {
    border-color: ${theme.color.danger.medium};
  }
`;

const SpaceFiller = styled.div`
  width: ${toRem(24)};
  height: ${toRem(24)};
`;

const TotalDuration = styled(Duration)`
  &.m-duration {
    display: flex;
    margin: ${toRem(12)} ${toRem(22)} 0 ${toRem(22)};
  }

  &.dt-duration {
    display: none;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    &.m-duration {
      display: none;
    }

    &.dt-duration {
      display: flex;
    }
  }
`;

const StyledInput = styled(Input)`
  &.list-item-field {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &::placeholder {
      color: ${theme.color.neutral[700]};
    }
  }
`;

const StyledText = styled(Text)`
  font-size: ${toRem(14)};
`;

const removeEmployeeMessage = (
  <>If you remove this employee, all their tippable hours will be deleted. Additionally, any tips they’ve collected will be excluded from calculations. As a result, they will neither receive nor contribute to tip distributions.</>
);

const getIsShareRemoveEmployeeMessage = () => (
  <>
    We're sorry, but you cannot remove this employee as they are currently assigned to receive a tip share from a Shared POS Job.
    <br />
    <br />
    Removing them would prevent us from disbursing 100% of the tips according to the site's tip policy. We're working to improve this limitation in the future.
  </>
);

const getHasSpitCheckEmployeeMessage = () => <>We're sorry, but you cannot remove this employee as they are currently associated with a Split Check.</>;

const getHighestPriorityError = (errors: IEmployeeLaborJobError[]) => {
  let error: IEmployeeLaborJobError;

  for (const e of errors) {
    if (!error) {
      error = e;
      continue;
    }

    if ((e.type || 0) > (error?.type || 0)) {
      error = e;
    }
  }

  return error;
};

const EmployeeListItem: React.FC<IEmployeeListItemProps> = ({
  className = '',
  isLoading,
  laborData,
  timecardData,
  paymentDate,
  timezone,
  userId,
  username,
  removeDisabled,
  onRemoveEmployee,
  onJobChange,
  validate,
  isShareRemovable = true,
  autoClockOut = false,
  hasSplitChecks = false,
}) => {
  const [jobsData, setJobsData] = useState<IEmployeeLaborJobState[]>(laborData);
  const [jobToRemove, setJobToRemove] = useState<IEmployeeLaborJobState | null>(null);
  const totalDuration = useMemo(() => jobsData.reduce((acc, jobData) => acc + jobData.duration, 0), [jobsData]);
  const [errors, setErrors] = useState<IEmployeeLaborJobError[] | null>(null);
  const [errorType, setErrorType] = useState<EmployeeLaborJobErrorType>(EmployeeLaborJobErrorType.TipCalcConfig);

  useEffect(() => {
    let newJobsData = [...jobsData];

    const validatedData = validate(jobsData);

    newJobsData = newJobsData.map((jobData, i) => ({
      ...jobData,
      error: validatedData[i].error,
    }));
    setJobsData(newJobsData);
  }, []);

  useEffect(() => {
    onJobChange(jobsData, jobToRemove);
    if (jobToRemove) setJobToRemove(null);

    const dataErrors = jobsData.filter((jobData) => jobData.error).map((jobData) => jobData.error);

    if (!dataErrors?.length) return setErrors(null);

    const maxError = getHighestPriorityError(dataErrors);
    setErrorType(maxError.type);
    setErrors(dataErrors);
  }, [jobsData]);

  const [showTimecardModal, hideTimecardModal] = useModal(() => <TimecardModal timecardData={timecardData} paymentDate={paymentDate} timezone={timezone} hideModal={hideTimecardModal} />, [timecardData, paymentDate, timezone]);

  const [showRemoveEmployeeModal, hideRemoveEmployeeModal] = useModal(() => {
    const handleRemovePayee = () => {
      onRemoveEmployee(userId);
      hideRemoveEmployeeModal();
    };

    const handleCancelRemovePayee = () => {
      hideRemoveEmployeeModal();
    };

    return (
      <ModalFactory customModal onClose={hideRemoveEmployeeModal} isActive>
        {isShareRemovable && !hasSplitChecks ? (
          <AlertContent
            title={`Remove ${username}?`}
            message={removeEmployeeMessage}
            leftActionLabel="Cancel"
            rightActionLabel="Remove"
            rightActionAppearance="destructive"
            onLeftAction={handleCancelRemovePayee}
            onRightAction={handleRemovePayee}
            maxWidth={420}
          />
        ) : (
          <AlertContent
            title="Unable to Remove Employee"
            message={hasSplitChecks ? getHasSpitCheckEmployeeMessage() : getIsShareRemoveEmployeeMessage()}
            rightActionLabel="Return to Payment"
            onRightAction={hideRemoveEmployeeModal}
            hideLeftAction
            maxWidth={420}
          />
        )}
      </ModalFactory>
    );
  }, [username, onRemoveEmployee, hasSplitChecks, isShareRemovable]);

  const [showRemoveJobModal, hideRemoveJobModal] = useModal(() => {
    if (!jobToRemove) return;

    const handleRemoveJob = () => {
      let newJobsData = jobsData.filter((jobData) => jobData.id !== jobToRemove.id);
      const validatedData = validate(newJobsData);

      newJobsData = newJobsData.map((jobData, i) => ({
        ...jobData,
        error: validatedData[i].error,
      }));

      setJobsData(newJobsData);
      hideRemoveJobModal();
    };

    const handleCancelRemoveJob = () => {
      setJobToRemove(null);
      hideRemoveJobModal();
    };

    return (
      <ModalFactory customModal onClose={handleCancelRemoveJob} isActive>
        <AlertContent
          title={`Remove job for ${username}?`}
          message="This will modify the payee's time worked. Are you sure you want to proceed?"
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          rightActionAppearance="destructive"
          onLeftAction={handleCancelRemoveJob}
          onRightAction={handleRemoveJob}
        />
      </ModalFactory>
    );
  }, [username, jobsData, jobToRemove, onRemoveEmployee]);

  const onDurationChange = useCallback(
    (index: number) =>
      ({ isSuccess, minutes, error }: IDurationResponse) => {
        let newJobsData = [...jobsData];

        if (isSuccess) {
          newJobsData[index].duration = minutes;

          const validatedData = validate(newJobsData);

          newJobsData = newJobsData.map((jobData, i) => ({
            ...jobData,
            error: validatedData[i].error,
          }));
        } else {
          newJobsData = newJobsData.map((jobData, i) => {
            if (i !== index) return jobData;

            return {
              ...jobData,
              duration: -1,
              error: {
                field: 'duration',
                message: error.message,
                type: EmployeeLaborJobErrorType.InvalidData,
              },
            };
          });
        }

        setJobsData(newJobsData);
      },
    [jobsData]
  );

  const onRemoveJobClick = useCallback(
    (job: IEmployeeLaborJobState) => () => {
      setJobToRemove(job);
      showRemoveJobModal();
    },
    []
  );

  const renderErrors = useMemo(() => {
    if (!errors) return;

    // using a set to ensure specific errors are only shown once
    const uniqueErrors: Set<string> = new Set();

    const errs: ReactNode[] = [];

    for (let i = 0; i < (errors || []).length; i++) {
      const error = errors[i];
      if (uniqueErrors.has(error.message)) continue;
      uniqueErrors.add(error.message);

      errs.push(
        <ErrorListItem key={`${error.type}-${i}`}>
          <Text size="small" fontWeight="medium">
            {error.message}
          </Text>
        </ErrorListItem>
      );
    }

    return errs;
  }, [errors]);

  const renderJobs = () => {
    const currentValidJobs = jobsData.filter((jobData) => jobData.duration !== 0);
    const removable = currentValidJobs.length > 1 && !hasSplitChecks ? 'removable' : '';

    const jobRows = jobsData.map((job, i) => (
      <JobRow key={`job-${job.id}`} className={removable}>
        <JobFieldWrapper>
          <JobRow className="m-row header-row">
            <JobsRowHeader>Job</JobsRowHeader>
          </JobRow>
          <StyledInput className="list-item-field" data-cy="job-title-field" placeholder={formatJobTitle(job.jobTitle?.title || '--')} disabled readOnly />
        </JobFieldWrapper>
        <JobFieldWrapper>
          <JobRow className="m-row header-row">
            <JobsRowHeader>Day Segment</JobsRowHeader>
          </JobRow>
          <StyledInput className="list-item-field" data-cy="day-segment-field" placeholder={job.daySegment?.name || '--'} disabled readOnly />
        </JobFieldWrapper>
        <JobFieldWrapper>
          <JobRow className="m-row header-row">
            <JobsRowHeader>Duration</JobsRowHeader>
          </JobRow>
          <StyledDurationInput id={`${`job-${job.id}`}-duration`} className={job.error?.field === 'duration' ? 'error' : ''} dataCy="duration-input" defaultValue={job.duration} onChange={onDurationChange(i)} />
        </JobFieldWrapper>

        {removable && (
          <>
            <RemoveJobIconButton data-cy="remove-job" className="remove-job-button" onClick={onRemoveJobClick(job)}>
              <Icon icon="Close" width={14} height={14} />
            </RemoveJobIconButton>

            <RemoveJobButton data-cy="remove-job" className="remove-job-button" onClick={onRemoveJobClick(job)}>
              Remove Job
            </RemoveJobButton>
          </>
        )}
      </JobRow>
    ));

    return (
      <JobsContainer>
        <JobRow className={`${removable} dt-row`}>
          <JobsRowHeader>Job</JobsRowHeader>
          <JobsRowHeader>Day Segment</JobsRowHeader>
          <JobsRowHeader>Duration</JobsRowHeader>
          {removable && <SpaceFiller className="remove-job-button" />}
        </JobRow>
        {jobRows}
      </JobsContainer>
    );
  };

  return (
    <EmployeeListItemLayout className={className} data-cy="employee-labor-data-item">
      {autoClockOut === true && (
        <ErrorRow className={`error-${EmployeeLaborJobErrorType.Other}`} data-cy="employee-labor-data-item-auto-clockout-error">
          <ErrorList>
            <StyledText size="small" fontWeight="medium">
              Auto clock out detected. Please verify time worked.
            </StyledText>
          </ErrorList>
        </ErrorRow>
      )}

      {errors && (
        <ErrorRow className={`error-${errorType}`} data-cy="employee-labor-data-item-error">
          <ErrorList>{renderErrors}</ErrorList>
        </ErrorRow>
      )}

      <TopRow>
        <UserInfo hideAvatar isLoading={isLoading} username={username} />

        <SecondaryControls>
          <TotalDuration className="dt-duration" duration={totalDuration} label="Tipped Hours" onClick={showTimecardModal} disabled={!timecardData} />
          <ButtonIcon data-cy="remove-btn" label="Remove" icon="Close" appearance="white" size="small" onClick={showRemoveEmployeeModal} disabled={removeDisabled} tooltipId={`remove-disabled-${userId}`} />
          {removeDisabled && <Tooltip id={`remove-disabled-${userId}`}>Must have at least one employee.</Tooltip>}
        </SecondaryControls>
      </TopRow>

      <TotalDuration className="m-duration" duration={totalDuration} label="Tipped Hours" onClick={showTimecardModal} disabled={!timecardData} />

      <BottomRow>{renderJobs()}</BottomRow>
    </EmployeeListItemLayout>
  );
};

export default EmployeeListItem;
